import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Block from "@store/Components/Common/Block";
import GridItem, { GridItemProps } from "@store/MobiusOverrides/GridItem/GridItem";
import { getShowNormalPrice } from "@store/Store/Context/ContextSelectors";
import { checkoutBlockProps, checkoutSubtitleProps } from "@store/theme/common/props/checkout";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import parse from "html-react-parser";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// Poolcorp Custom Code
import StoreApplicationState from "@store/Store/StoreApplicationState";
import getBooleanProperty from "@store/utilities/getBooleanProperty";
// Poolcorp Custom Code

const mapStateToProps = (state: StoreApplicationState) => ({ // Poolcorp Custom Code
    language: state.context.session.language,
    showNormalPrice: getShowNormalPrice(state),
    // Poolcorp Custom Code
    renderPaymentHistory: getBooleanProperty(state.context.session.properties.renderPaymentHistory),
});

type Props = ReturnType<typeof mapStateToProps>;

interface InvoicePayment {
    id: string;
    checkNumber: string;
    paymentDate: Date;
    checkAmountDisplay: string;
    cashAmountDisplay: string;
}

export interface InvoiceDetailsPaymentsHistoryTableStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    title?: TypographyPresentationProps;
    tableWrapper?: InjectableCss;
    tableGridHead?: GridContainerProps;
    tableGridBody?: GridContainerProps;
    tableGridRow?: GridItemProps;
    tableGridRowContainer?: GridContainerProps;
    tableGridCellText?: TypographyPresentationProps;
    mobileHeader?: TypographyPresentationProps;
    mobileHeaderHidden?: HiddenProps;
    headerText?: TypographyProps;
    checkNumberGridHeader?: GridItemProps;
    transactionDateGridHeader?: GridItemProps;
    checkAmountGridHeader?: GridItemProps;
    cashAmountGridHeader?: GridItemProps;
    checkNumberGridCell?: GridItemProps;
    transactionDateGridCell?: GridItemProps;
    mobileTransactionDateGridCell?: GridItemProps;
    checkAmountGridCell?: GridItemProps;
    cashAmountGridCell?: GridItemProps;
}

export const paymentsHistoryTableStyles: InvoiceDetailsPaymentsHistoryTableStyles = {
    title: checkoutSubtitleProps,
    centeringWrapper: tableGridProps.centeringWrapper,
    spinner: tableGridProps.spinner,
    tableWrapper: tableGridProps.container,
    tableGridHead: tableGridProps.tableGridHead,
    tableGridBody: tableGridProps.tableGridBody,
    tableGridRow: {
        ...tableGridProps.tableGridRow,
        css: css`
            ${tableGridProps.tableGridRow?.css}
            span:last-child {
                display: block;
            }
        `
    },
    tableGridRowContainer: tableGridProps.tableGridRowContainer,
    tableGridCellText: { 
        ...tableGridProps.tableGridCellText,
        css: css`
            word-wrap: normal;
            align-items: center;
        `
    },
    mobileHeader: tableGridProps.mobileHeader,
    mobileHeaderHidden: tableGridProps.mobileHeaderHidden,
    headerText: tableGridProps.textHeader,
    checkNumberGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 3, 3],
    },
    transactionDateGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 3, 3],
    },
    checkAmountGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 3, 3],
        css: css`
            ${tableGridProps.tableGridHeader?.css}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, "end"], properties: ["justify-content"] },
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    cashAmountGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 3, 3],
        css: css`
            ${tableGridProps.tableGridHeader?.css}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, "end"], properties: ["justify-content"] },
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    checkNumberGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 3, 3],
    },
    transactionDateGridCell: {
        ...tableGridProps.tableGridCell,
        width: [0, 0, 0, 3, 3],
    },
    mobileTransactionDateGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 0, 0],
    },
    checkAmountGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 3, 3],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, "end"], properties: ["justify-content"] },
                    ],
                    mediaOptions.max,
                )}
        `,
    },
    cashAmountGridCell: {
        ...tableGridProps.tableGridCell,
        width: [6, 6, 6, 3, 3],
        css: css`
            ${tableGridProps.tableGridCell?.css}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, null, "end"], properties: ["justify-content"] },
                    ],
                    mediaOptions.max,
                )}
        `,
    },
};

const styles = paymentsHistoryTableStyles;

const Content: FC<Props> = ({ language, showNormalPrice, renderPaymentHistory }: Props) => {
    const { value: invoice, isLoading } = useContext(InvoiceStateContext);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    let invoicePayments: InvoicePayment[] | undefined;
    if (invoice?.properties.paymentsHistory) {
        try {
            invoicePayments = JSON.parse(invoice?.properties.paymentsHistory);
        } catch (error) {} // eslint-disable-line no-empty
    }

    if (!invoicePayments || invoicePayments.length === 0) {
        return <Typography>{translate("No Payments made yet.")}</Typography>;
    }

    const tableHeaderFormat = (title:string) => {
        return title
            .replace(" #", "&nbsp;#")
            .split(" ")
            .map((s, i) => parse(`${i>0?`<span>&nbsp;</span>`:''}<span>${s}</span>`, parserOptions));
    };

    return (
        <StyledWrapper {...styles.tableWrapper}>
            <GridContainer {...styles.tableGridHead}>
                <GridItem {...styles.checkNumberGridHeader}>
                    <Typography {...styles.headerText}>
                        {tableHeaderFormat(translate("Check #"))}
                    </Typography>
                </GridItem>
                <GridItem {...styles.transactionDateGridHeader}>
                    <Typography {...styles.headerText}>
                        {tableHeaderFormat(translate("Transaction Date"))}
                    </Typography>
                </GridItem>
                <GridItem {...styles.checkAmountGridHeader}>
                    <Typography {...styles.headerText}>
                        {tableHeaderFormat(translate("Check Amount"))}
                    </Typography>
                </GridItem>
                <GridItem {...styles.cashAmountGridHeader}>
                    <Typography {...styles.headerText}>
                        {tableHeaderFormat(translate("Cash Amount"))}
                    </Typography>
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.tableGridBody}>
                {invoicePayments.map(invoicePayment => (
                    <GridItem 
                        {...styles.tableGridRow}
                        key={invoicePayment.id}
                    >
                        <GridContainer {...styles.tableGridRowContainer}>
                            <GridItem {...styles.checkNumberGridCell}>
                                <Hidden {...styles.mobileHeaderHidden}>
                                    <Typography {...styles.mobileHeader}>
                                        {`${translate("Check #")}: `}
                                    </Typography>
                                </Hidden>
                                <Typography {...styles.tableGridCellText}>
                                    {invoicePayment.checkNumber}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.transactionDateGridCell}>
                                <Typography {...styles.tableGridCellText}>
                                    {getLocalizedDateTime({ dateTime: new Date(invoicePayment.paymentDate), language })}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.checkAmountGridCell}>
                                <Hidden {...styles.mobileHeaderHidden}>
                                    <Typography {...styles.mobileHeader}>
                                        {`${translate("Check Amount")}: `}
                                    </Typography>
                                </Hidden>
                                <Typography {...styles.tableGridCellText}>
                                    {invoicePayment.checkAmountDisplay}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.mobileTransactionDateGridCell}>
                                <Hidden {...styles.mobileHeaderHidden}>
                                    <Typography {...styles.mobileHeader}>
                                        {`${translate("Transaction Date")}: `}
                                    </Typography>
                                </Hidden>
                                <Typography {...styles.tableGridCellText}>
                                    {getLocalizedDateTime({ dateTime: new Date(invoicePayment.paymentDate), language })}
                                </Typography>
                            </GridItem>
                            <GridItem {...styles.cashAmountGridCell}>
                                <Hidden {...styles.mobileHeaderHidden}>
                                    <Typography {...styles.mobileHeader}>
                                        {`${translate("Cash Amount")}: `}
                                    </Typography>
                                </Hidden>
                                <Typography {...styles.tableGridCellText}>
                                    {invoicePayment.cashAmountDisplay}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const InvoiceDetailsPaymentsHistoryTable: FC<Props> = props => {
    // Poolcorp Custom Code - Display payment history according to session property
    if (!props.renderPaymentHistory || !props.showNormalPrice) {
        return null;
    }

    return (
        <Block {...checkoutBlockProps}>
            <Typography {...styles.title}>{translate("Payment Information")}</Typography>
            <Content {...props} />
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InvoiceDetailsPaymentsHistoryTable),
    definition: {
        group: "Invoice History",
        displayName: "Payment Information Table",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
